import { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import {
  AgxBadge,
  AgxButton,
  AgxDatePicker,
  AgxDivider,
  AgxSpacer,
  AgxTextInput,
  AgxTypography,
  toDateMediumString,
} from '@urbanx/agx.ui-components2';
import {
  CoolingOffType,
  Deposit,
  DepositType,
  ValueType,
  Condition,
  ConditionType,
  useStaffGetContractDates,
  useStaffGetSaleInformation,
} from '@urbanx/agx.campaigns.client';
import { SubPagePanel } from 'components/panel/SubPagePanel';
import ConditionDate from '../components/ConditionDate';
import { EditConditionModal } from '../modals/EditConditionModal';
import { useFormConditionDueDate } from '../components/useFormConditionDueDate';
import { parseNumber } from 'utils/parseNumber';
import { useSelectedContract } from 'hooks';
import { useGetSpecialConditionsInfo } from 'hooks/useGetSpecialConditionsInfo';
import { getDepositWithEditedCondition, normalizeDepositToDollar } from '../depositCalculations';
import { getContractInfoDueDateBadgeValue } from '../components/contractInformationDueDate';
import TaskDueDateBadge from 'components/tasks/TaskDueDateBadge';

interface DepositPanelProps {
  id: string;
  panelTitle: string;
  isNsw: boolean;
  deposit?: Deposit;
  onChange: (deposit: Deposit | undefined) => void;
}

export function DepositPanel({ id, panelTitle, isNsw, deposit, onChange }: DepositPanelProps) {
  const { contract, campaignId } = useSelectedContract();

  const [editDeposit, setEditDeposit] = useState(false);
  const [showEditDateModal, setShowEditDateModal] = useState(false);
  const [depositValueType, setDepositValueType] = useState<ValueType>(
    deposit?.depositValueType || ValueType.Dollar
  );
  const [depositAmount, setDepositAmount] = useState<number | undefined>(deposit?.amount);
  const [depositPercent, setDepositPercent] = useState<number | undefined>(deposit?.percent);
  const [editErrorMessage, setEditErrorMessage] = useState<string | undefined>(undefined);
  const dueDate = useFormConditionDueDate(deposit);

  const { specialConditionIdToClauseNumberMap } = useGetSpecialConditionsInfo(
    campaignId,
    contract?.contractId
  );

  const {
    data: saleInformation,
    isPending,
    isError,
  } = useStaffGetSaleInformation(
    campaignId || '',
    contract?.contractId || '',
    !!campaignId && !!contract?.contractId
  );

  const { data: contractDates } = useStaffGetContractDates(
    campaignId || '',
    contract?.contractId || '',
    !!campaignId && !!contract?.contractId && isNsw && deposit?.depositType === DepositType.Balance
  );

  const displayDeposit = useMemo(() => {
    if (!isNsw || deposit?.depositType !== DepositType.Balance || !dueDate.dueDateString) {
      return deposit;
    }

    const isContractDate =
      contractDates?.coolingOffDetails?.coolingOffType === CoolingOffType.NoCoolingOff &&
      dueDate.dueDateString === contractDates?.contractDate;

    return {
      ...deposit,
      condition: Condition.OnDate,
      conditionType: isContractDate ? ConditionType.Contract : ConditionType.Unconditional,
      referenceDate: isContractDate ? undefined : dueDate.dueDateString,
    };
  }, [contractDates, deposit, dueDate.dueDateString, isNsw]);

  const normalizedNswDeposit = useMemo(
    () => (isNsw ? normalizeDepositToDollar(deposit, saleInformation?.salePrice) : deposit),
    [deposit, isNsw, saleInformation?.salePrice]
  );

  useEffect(() => {
    setDepositValueType(normalizedNswDeposit?.depositValueType || ValueType.Dollar);
    setDepositPercent(normalizedNswDeposit?.percent);
    setDepositAmount(normalizedNswDeposit?.amount);
  }, [normalizedNswDeposit]);

  useEffect(() => {
    if (!isPending && !isError && saleInformation) {
      if (
        depositValueType === ValueType.Percentage &&
        saleInformation.salePrice &&
        depositPercent
      ) {
        const calculatedAmount = (saleInformation.salePrice * depositPercent) / 100;
        setDepositAmount(calculatedAmount);
      }
    }
  }, [saleInformation, depositValueType, depositPercent, isPending, isError]);

  const isEditingPercentageDeposit = depositValueType === ValueType.Percentage;

  const onSaveEditAmount = useCallback(() => {
    if (editDeposit) {
      if (isEditingPercentageDeposit) {
        if (depositPercent === undefined || depositPercent < 0.1) {
          setEditErrorMessage('Minimum percentage is 0.1%');
          return;
        }
        setEditErrorMessage(undefined);
        onChange({
          ...deposit,
          percent: depositPercent,
          amount: undefined,
          depositValueType: ValueType.Percentage,
        });
      } else {
        if (depositAmount === undefined || depositAmount < 1) {
          setEditErrorMessage('Minimum amount is $1');
          return;
        }
        setEditErrorMessage(undefined);
        onChange({
          ...deposit,
          amount: depositAmount,
          percent: undefined,
          depositValueType: ValueType.Dollar,
        });
      }
    } else {
      setEditErrorMessage(undefined);
    }
    setEditDeposit((prev) => !prev);
  }, [editDeposit, onChange, deposit, depositAmount, depositPercent, isEditingPercentageDeposit]);

  if (!deposit) return;

  const dueDateValue = dueDate?.dueDate ? dayjs(dueDate.dueDate).format('YYYY-MM-DD') : '';
  const dueDateBadgeValue = getContractInfoDueDateBadgeValue(dueDate?.dueDate);

  const displayLabel =
    isEditingPercentageDeposit && depositPercent
      ? `${panelTitle} (${depositPercent}%)`
      : panelTitle;

  return (
    <SubPagePanel
      key={`deposit-panel-${id}`}
      expandable
      defaultExpanded
      customTitle={
        <AgxSpacer direction="horizontal" gap={'xs'}>
          <AgxTypography
            id={`panelTitle_${panelTitle}`}
            type="title"
            size="large"
            color="var(--text-primary)"
          >
            {panelTitle}
          </AgxTypography>
          {deposit.completedDate ? (
            <AgxBadge
              type="colourfill"
              colour="green"
              size="md"
              text={`Paid ${toDateMediumString(deposit.completedDate)}`}
            />
          ) : (
            dueDateBadgeValue && (
              <TaskDueDateBadge dueDate={dueDateBadgeValue} isCompleted={false} />
            )
          )}
        </AgxSpacer>
      }
    >
      <AgxSpacer
        direction="horizontal"
        gap="spacing-md"
        fill
        padding={{ top: 'spacing-md', bottom: 'spacing-3xl' }}
      >
        <ConditionDate
          id={id}
          condition={displayDeposit ?? deposit}
          isDeposit
          depositAmount={depositAmount}
          specialConditionIdToClauseNumberMap={specialConditionIdToClauseNumberMap ?? {}}
        />
      </AgxSpacer>
      <AgxDivider variant="horizontal" />
      <AgxSpacer
        direction="vertical"
        gap="spacing-md"
        fill
        padding={{ top: 'spacing-3xl', bottom: 'spacing-3xl' }}
      >
        <AgxSpacer direction="horizontal" gap="spacing-md" fill contentAlignment="end">
          {isEditingPercentageDeposit && (
            <AgxTextInput
              id={`${id}-percent`}
              type="percent"
              stretch
              disabled={!editDeposit}
              hideOptionalText
              label={displayLabel}
              defaultValue={depositPercent?.toString()}
              onChange={(value) => setDepositPercent(parseNumber(value))}
            />
          )}
          {!isEditingPercentageDeposit && (
            <AgxTextInput
              id={`${id}-amount`}
              type="currency"
              stretch
              disabled={!editDeposit}
              hideOptionalText
              label={displayLabel}
              defaultValue={depositAmount?.toString()}
              onChange={(value) => setDepositAmount(parseNumber(value))}
            />
          )}
          <AgxButton
            id={`${id}-edit-amount`}
            variant="hollow"
            text={editDeposit ? 'Save' : 'Edit'}
            onClick={onSaveEditAmount}
          />
        </AgxSpacer>
        {editErrorMessage && (
          <AgxTypography id={`${id}-edit-error`} type="form_error" size="small">
            {editErrorMessage}
          </AgxTypography>
        )}
      </AgxSpacer>
      <AgxSpacer
        direction="horizontal"
        gap="spacing-md"
        fill
        padding={{ top: 'spacing-md', bottom: 'spacing-3xl' }}
        contentAlignment="end"
      >
        <AgxDatePicker
          id={`${id}-due-date`}
          stretch
          disabled={true}
          optionalText=""
          label="Due Date"
          key={dueDateValue}
          value={dueDateValue}
        />
        <AgxButton
          id={`${id}-edit-due-date`}
          variant="hollow"
          text={'Edit'}
          onClick={() => {
            setShowEditDateModal(true);
          }}
        />
      </AgxSpacer>
      <AgxSpacer
        direction="horizontal"
        gap="spacing-md"
        fill
        padding={{ top: 'spacing-md', bottom: 'spacing-md' }}
        contentAlignment="end"
        contentJustification="end"
      >
        <AgxButton
          id={`${id}-delete`}
          variant="link"
          danger
          text="Delete"
          onClick={() => onChange(undefined)}
        />
      </AgxSpacer>
      {showEditDateModal && (
        <EditConditionModal
          id={id}
          modalTitle="Edit Condition"
          formCondition={deposit}
          availableConditions={[
            Condition.NdaysFromDate,
            Condition.OnSpecificDate,
            Condition.FinanceBeingSatisfied,
            Condition.BuildingAndPestBeingSatisfied,
            Condition.UponSatisfactionOfSpecialCondition,
          ]}
          openModal={setShowEditDateModal}
          onChange={(updatedDeposit) => {
            onChange(
              getDepositWithEditedCondition({
                deposit,
                updatedDeposit: updatedDeposit as Deposit,
                isNsw,
                salePrice: saleInformation?.salePrice,
                amount: depositAmount,
                percent: depositPercent,
              })
            );
            setShowEditDateModal(false);
          }}
          depositType={deposit?.depositType}
        />
      )}
    </SubPagePanel>
  );
}
